import React, { useState, useCallback, useEffect } from "react";
import BasicInformation from "./AssessmentProfileBasic";
import AssessmentParameters from "./AssessmentProfileParameters";
import QuestionTypeWeightings from "./AssessmentQuestionTypeWeightings";
import AssessmentProfileService from "./services/AssessmentProfileService";
import ProfileQuestions from "./components/assessmentProfile/ProfileQuestions";
import { Check, ArrowLeft } from "lucide-react";
import { useNotification } from "./CustomNotificationSystem";
import { Link } from "react-router-dom";
import QuestionBuilder from "./QuestionnaireBuilder";
import { generateUniqueId } from "./utils";

const AsessmentProfileForm = ({
  company,
  currentUser,
  setCurrentView,
  profileId = null,
  duplicate = false,
}) => {
  const notify = useNotification();
  const [profile, setProfile] = useState({
    basic: {
      name: "",
      scope: "Custom",
      description: "",
      is_default: false,
    },
    parameters: {
      question_count: 15,
      duration: 90,
      difficulty_level: 5,
    },
    question_weight: {
      multiple_choice: 4,
      open_ended: 3,
      scenario_based: 8,
      technical_skills: 10,
      behavioral: 3,
      document_upload: 4,
    },
    company: company._id,
    user: currentUser.id,
  });
  const [tempJobId] = useState(generateUniqueId()); //we have to pass this for the sake of simplicity, as questions are strictly saved against the jobs
  const [enableProfileGuide, setEnableProfileGuide] = useState(false);
  const handleShowProfileGuide = () => {
    setEnableProfileGuide(!enableProfileGuide);
  };

  const handleChange = (profile, type) => {
    setProfile((prev) => ({
      ...prev,
      [type]: { ...prev[type], ...profile }, // Update the specific key dynamically
    }));
  };

  const loadProfile = useCallback(async () => {
    if (!profileId) return false;
    const profile = await AssessmentProfileService.get(profileId);

    if (duplicate) {
      delete profile._id;
      profile.basic.name = "";
      profile.basic.description = "";
      profile.basic.is_default = false;
    }
    setProfile(profile);
  }, [profileId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const submitProfile = async () => {
    const validate = AssessmentProfileService.validate(profile);
    if (!validate.isValid) {
      const errorMessages =
        AssessmentProfileService.displayValidationErrors(validate);
      notify(errorMessages, "error");
      return false;
    }
    await AssessmentProfileService.add(profile);
    if (!profileId || duplicate) {
      notify("Profile added", "success");
      setCurrentView("assessmentProfile");
    } else {
      notify("Profile updated", "success");
    }
  };

  return (
    <div>
      <div className="container mx-auto p-4 lg:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-indigo-800">
            {profileId && !duplicate ? "Update Profile" : "Add New Profile"}
          </h2>
          <Link
            className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors font-medium"
            to="/assessment/profile"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </Link>
        </div>
        {profileId && !profile.approved ? (
          <div className="bg-indigo-200 sm:rounded-lg border overflow-hidden transform transition-all duration-300 hover:shadow-md mb-3 p-5">
            <label className="text-sm font-bold">
              Profile approval required. Save the profile to approve it.
              Approval is necessary to generate a questionnaire for this profile
            </label>
          </div>
        ) : (
          ""
        )}
        <div className="mb-8">
          <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md">
            <div className="px-4 py-5 sm:p-6">
              <div className="sm:flex sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Assessment Profile Creation Guide
                  </h3>
                  <div className="mt-2 max-w-xl text-sm text-gray-500">
                    <p>
                      An Assessment Profile serves as a reusable template for
                      evaluating candidates with similar skill requirements.
                    </p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm transition-all duration-200"
                    onClick={handleShowProfileGuide}
                  >
                    {enableProfileGuide ? "Hide Guide" : "Show Guide"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`${!enableProfileGuide ? "hidden" : ""} mt-4 animate-fadeIn`}
          >
            <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
              <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-blue-50 to-white">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Profile Creation Best Practices
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Follow these guidelines to create effective assessment
                  profiles
                </p>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Profile Naming
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      Use specific, descriptive names that clearly identify the
                      role and experience level (e.g., "Frontend Developer -
                      Senior Level" instead of just "Developer").
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Question Balance
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      Balance technical skills (50-70%) with soft skills and
                      cultural fit (30-50%) for most roles. Adjust based on
                      seniority level - more senior roles typically require more
                      soft skills assessment.
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Assessment Duration
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      Keep assessments under 45 minutes for most roles to
                      minimize candidate drop-off. For specialized or senior
                      positions, consider 60 minutes maximum.
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Question Types
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      Include a mix of question types for comprehensive
                      evaluation:
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li>
                          Multiple choice questions for quick knowledge checks
                        </li>
                        <li>
                          Scenario-based questions to evaluate critical thinking
                        </li>
                        <li>
                          Open-ended questions to assess communication skills
                        </li>
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div>
          <BasicInformation
            profile={profile.basic}
            handleChange={handleChange}
          />
        </div>
        <div className="mt-8">
          <AssessmentParameters
            profile={profile.parameters}
            handleChange={handleChange}
          />
        </div>
        <div className="mt-8">
          <QuestionTypeWeightings
            profile={profile.question_weight}
            handleChange={handleChange}
          />
        </div>
        {profileId && !duplicate && (
          <div>
            <div className="mt-8">
              <ProfileQuestions profileId={profileId} />
            </div>
            <div className="mt-8 bg-white shadow sm:rounded-lg overflow-hidden transform transition-all duration-300 hover:shadow-md">
              <QuestionBuilder
                tempJobId={tempJobId}
                forAssessmentProfile={profileId}
              />
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 sticky bottom-0 bg-white p-4 border-t border-gray-200 shadow-md rounded-lg z-10 mt-2">
          <div className="flex space-x-4">
            <button
              onClick={submitProfile}
              className="flex items-center gap-2 bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600 transition-colors sm:text-sm"
            >
              <Check /> Save Profile
            </button>
            <Link
              className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors sm:text-sm"
              to="/assessment/profile"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsessmentProfileForm;
